<template>
    <validation-observer ref="amenity_metadata_form" v-slot="{ handleSubmit, errors, meta }">
        <modal
            :scrollable="true"
            :title="modalTitle"
            :id="modalId"
            @shown="setValues"
            @ok.prevent="handleSubmit(_, createUpdateAmenity)"
            :ok-disabled="!meta.valid"
        >
            <validation-provider name="amenity" rules="required">
                <form-select
                    :value="amenityObj.amenity"
                    label="Amenity"
                    :options="amenityOptions"
                    @change="changeAmenity"
                    :error="errors.amenity"
                ></form-select>
            </validation-provider>
            <form-textarea rows="2" v-model="amenityObj.instructions" label="Instructions" />
            <div v-for="option in metaDataOptions" :key="option.id">
                <form-search-multiselect
                    v-if="option.type === MULTISELECT_TYPE"
                    class="mt-2 mb-2"
                    :value="amenityObj.metaData[option.type][option.key] || []"
                    :options="option.options"
                    placeholder="Please select"
                    :label="option.label"
                    @input="(value) => onChangeInput(value, option.key, option.type)"
                >
                </form-search-multiselect>
                <form-select
                    v-if="
                        option.hideIf
                            ? !option.hideIf(amenityObj.metaData) && option.type === SELECT_TYPE
                            : option.type === SELECT_TYPE
                    "
                    :value="amenityObj.metaData[option.type][option.key] || ''"
                    :options="option.options"
                    :label="option.label"
                    placeholder="Please Select"
                    @change="(value) => onChangeInput(value, option.key, option.type)"
                >
                </form-select>
                <form-input
                    type="number"
                    v-if="
                        option.hideIf
                            ? !option.hideIf(amenityObj.metaData) && option.type === NUMBER_TYPE
                            : option.type === NUMBER_TYPE
                    "
                    :value="amenityObj.metaData[option.type][option.key] || ''"
                    :label="option.label"
                    @change="(value) => onChangeInput(value, option.key, option.type)"
                />
                <form-input
                    v-if="
                        option.hideIf
                            ? !option.hideIf(amenityObj.metaData) && option.type === TEXT_TYPE
                            : option.type === TEXT_TYPE
                    "
                    :value="amenityObj.metaData[option.type][option.key] || ''"
                    :label="option.label"
                    @change="(value) => onChangeInput(value, option.key, option.type)"
                />
                <div v-if="option.type === DATERANGE_TYPE" class="pt-2 pb-2">
                    <form-datepicker
                        v-for="dateField in option.options"
                        :label="dateField.text"
                        :key="dateField.value"
                        :hide-header="true"
                        :display-format="userUnicodeDateFormat"
                        :value="
                            (amenityObj.metaData[option.type][option.key] &&
                                amenityObj.metaData[option.type][option.key][dateField.value]) ||
                            ''
                        "
                        @input="(value) => onChangeInput(value, option.key, option.type, dateField.value)"
                    />
                </div>
                <div v-if="option.type === TIMERANGE_TYPE" class="pt-2 pb-2">
                    <div v-for="dateField in option.options" :key="dateField.value">
                        <label :for="dateField.text">{{ dateField.text }}</label>
                        <b-timepicker
                            :id="dateField.text"
                            :value="
                                (amenityObj.metaData[option.type][option.key] &&
                                    amenityObj.metaData[option.type][option.key][dateField.value]) ||
                                ''
                            "
                            @input="(value) => onChangeInput(value, option.key, option.type, dateField.value)"
                        />
                    </div>
                </div>
            </div>
            <validation-provider name="name" rules="required">
                <form-input
                    :error="errors.name"
                    :value="amenityObj.name"
                    label="Name"
                    class="mb-0"
                    @change="onChangeName"
                />
                <div class="text-small">
                    <small>This field is for differentiation purposes only. It is only used on this page</small>
                </div>
            </validation-provider>
        </modal>
    </validation-observer>
</template>

<script>
    import API from '../../../api.jsx';
    import { AMENITIES_META_DATA_LIST_ENDPOINT } from '../../../endpoints';
    import FormSelect from '../../generic/form-select/form-select.vue';
    import Modal from '../../generic/modal/modal.vue';
    import FormSearchMultiselect from '../../generic/form-search-multiselect/form-search-multiselect.vue';
    import FormInput from '../../generic/form-input/form-input.vue';
    import FormTextarea from '../../generic/form-textarea/form-textarea.vue';
    import FormDatepicker from '../../generic/form-datepicker/form-datepicker.vue';
    import { mapGetters } from 'vuex';
    import {
        getAmenityAdditionalMetaDataOptions,
        parseMetaData,
        SELECT_TYPE,
        MULTISELECT_TYPE,
        NUMBER_TYPE,
        TEXT_TYPE,
        DATERANGE_TYPE,
        TIMERANGE_TYPE,
        generateAmenityMetadataName,
    } from './helpers.js';

    const defaultAmenityObj = {
        id: 1,
        name: null,
        amenity: null,
        instructions: null,
        metaData: {
            number: {},
            select: {},
            multiselect: {},
            text: {},
            date_range: {},
            time_range: {},
        },
    };

    export default {
        components: {
            Modal,
            FormSelect,
            FormSearchMultiselect,
            FormInput,
            FormTextarea,
            FormDatepicker,
        },
        props: {
            values: Object,
            modalId: String,
        },
        data() {
            return {
                amenitiesList: [],
                amenityObj: { ...JSON.parse(JSON.stringify(defaultAmenityObj)) },
                SELECT_TYPE,
                MULTISELECT_TYPE,
                NUMBER_TYPE,
                TEXT_TYPE,
                DATERANGE_TYPE,
                TIMERANGE_TYPE,
            };
        },
        computed: {
            ...mapGetters('header', ['userUnicodeDateFormat']),

            amenityOptions() {
                return this.amenitiesList.map((item) => ({
                    value: `${item.key_name}-${item.id}`,
                    text: item.name,
                }));
            },
            metaDataOptions() {
                return this.amenityObj.amenity ? this.getAmenityAdditionalMetaDataOptions(this.amenityObj.amenity) : [];
            },
            modalTitle() {
                return this.values ? `Edit Metadata` : `Add Metadata`;
            },
        },
        methods: {
            getAmenityAdditionalMetaDataOptions,
            generateAmenityMetadataName,
            async setValues() {
                await this.fetchAmenities();
                if (this.values) {
                    const amenityId = this.amenitiesList.find((item) => item.name === this.values.amenity)?.id;
                    const metaData = this.values?.metadata;
                    const metadataObj = metaData
                        ? parseMetaData(metaData, this.values?.key_name, this.values?.instructions)
                        : {};
                    this.amenityObj = {
                        ...this.values,
                        amenity: `${this.values.key_name}-${amenityId}`,
                        metaData: {
                            ...JSON.parse(JSON.stringify(defaultAmenityObj.metaData)),
                            ...metadataObj,
                        },
                    };
                } else {
                    this.amenityObj = {
                        ...JSON.parse(JSON.stringify(defaultAmenityObj)),
                    };
                }
                this.$refs.amenity_metadata_form.setValues({
                    name: this.amenityObj.name,
                    amenity: this.amenityObj.amenity,
                });
            },
            async fetchAmenities() {
                try {
                    const { data } = await API.get(`${AMENITIES_META_DATA_LIST_ENDPOINT()}amenities-list/`);
                    this.amenitiesList = data;
                } catch (e) {
                    this.errorToast({ text: 'Could not fetch amenities' });
                } finally {
                    this.busy = false;
                }
            },
            createUpdateAmenity() {
                this.$emit('submit', this.amenityObj, this.amenitiesList);
            },
            changeAmenity(value) {
                const name = value.includes('-') ? value.split('-')[0] : '';
                const metaDataName = this.amenitiesList.find((item) => item.key_name === name);
                this.amenityObj = {
                    ...defaultAmenityObj,
                    amenity: value,
                    name: metaDataName?.name || '',
                };
                this.$refs.amenity_metadata_form.setFieldValue('amenity', value);
                this.$refs.amenity_metadata_form.setFieldValue('name', this.amenityObj.name);
            },
            onChangeName(value) {
                this.amenityObj = {
                    ...this.amenityObj,
                    name: value,
                };
                this.$refs.amenity_metadata_form.setFieldValue('name', value);
            },
            onChangeInput(value, name, fieldName, subKey) {
                const hidePriceCondition = name === 'price_category' && value === 'free'; //hide fields and clean fields if true
                if (fieldName === 'date_range' || fieldName === 'time_range') {
                    this.amenityObj = {
                        ...this.amenityObj,
                        metaData: {
                            ...this.amenityObj.metaData,
                            [fieldName]: {
                                ...this.amenityObj.metaData[fieldName],
                                [name]: {
                                    ...this.amenityObj.metaData[fieldName][name],
                                    [subKey]: value,
                                },
                            },
                        },
                    };
                } else {
                    if (hidePriceCondition) {
                        this.amenityObj = {
                            ...this.amenityObj,
                            metaData: {
                                ...this.amenityObj.metaData,
                                [fieldName]: {
                                    ...this.amenityObj.metaData[fieldName],
                                    [name]: value,
                                },
                            },
                        };
                        this.amenityObj.metaData.number.amount = '';
                        this.amenityObj.metaData.select.price_window = '';
                        this.amenityObj.metaData.text.currency = '';
                    } else {
                        this.amenityObj = {
                            ...this.amenityObj,
                            metaData: {
                                ...this.amenityObj.metaData,
                                [fieldName]: {
                                    ...this.amenityObj.metaData[fieldName],
                                    [name]: value,
                                },
                            },
                        };
                    }
                }
                const amenityName = this.amenitiesList.find(
                    (item) => item.key_name === this.amenityObj.amenity.split('-')[0]
                )?.name;
                this.amenityObj = {
                    ...this.amenityObj,
                    name: this.generateAmenityMetadataName(this.amenityObj, amenityName),
                };
            },
        },
    };
</script>
